import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DateFnsUtils from "@date-io/date-fns";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DeviceStats } from "../DeviceStats/DeviceStats";
import { StyledTab, StyledTabs, useStyles } from "./AnalyzerAdoptionTab.styles";
import { SectionHeader } from "../SectionHeader/SectionHeader";
import { TabPanel } from "../TabPanel/TabPanel";
import { Loader } from "../Loader/Loader";
import {
  commonActions,
  fuseAdoptionActions,
  hmplusGlobalReleaseActions,
} from "../../_redux/_actions";
import { dateFormatSlashed, dateFormatMui, roles } from "../../_constants";
import { fuseAdoptionService } from "../../_services/fuse-adoption.service";
import { addMonths, subMonths } from "date-fns";
import { clampToTwoMonths } from "../../_helpers";
import { Toast } from "../Toast/Toast";
import { Error } from "@material-ui/icons";

const analyzerMetaMap = {
  hmplus: {
    target: "HM_PLUS",
    segment: "hmplus-updates",
    text: "HM+",
    key: "hmplus",
  },
  hm5: { target: "HM5", segment: "hm5-updates", text: "HM5", key: "hm5" },
  sa: { target: "SA", segment: "sa-updates", text: "SA", key: "sa" },
  ua: { target: "UA", segment: "ua-updates", text: "UA", key: "ua" },
  vs2nga: {
    target: "VS2NGA",
    segment: "vs2nga-updates",
    text: "VS2NGA",
    key: "vs2nga",
  },
  vs2jen2: {
    target: "VS2JEN2",
    segment: "vs2jen2-updates",
    text: "VS2JEN2",
    key: "vs2jen2",
  },
};

function AnalyzerAdoption() {
  const { t } = useTranslation(["analyserUpdates", "common"]);
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const path = location.pathname.replace(/\/$/, "");
  const segments = path.split("/");
  const secondLastSegment = segments[segments.length - 2];
  const baseKey = secondLastSegment.replace(/-updates$/, "").toLowerCase();
  const [open, setOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const analyzerInfo = analyzerMetaMap[baseKey];

  const receivedState = location.state || {};
  const initialVersion = receivedState.version || "";
  const initialDate = receivedState.uploadedDate
    ? new Date(receivedState.uploadedDate).getTime()
    : Date.now();

  const [tabIndex, setTabIndex] = useState(0);
  const [version, setVersion] = useState(initialVersion);
  const [fromDate, setFromDate] = useState(clampToTwoMonths(initialDate));
  const [toDate, setToDate] = useState(Date.now());
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(true);

  const { ROLE_SUPPORT_L1, ROLE_SUPPORT_L2 } = roles;
  const { hmplusGlobalReleasesListData } = useSelector(
    (state) => state.commonReducer
  );

  const { fuseAdoptionData } = useSelector(
    (state) => state.fuseAdoptionReducer
  );

  const { hmplusGlobalReleaseData } = useSelector(
    (state) => state.hmplusGlobalReleaseReducer
  );

  const user = useSelector((state) => state.authentication.user);
  const isSupportL1L2Role = user.roles.some(
    (item) => item == ROLE_SUPPORT_L1 || item == ROLE_SUPPORT_L2
  );
  useEffect(() => {
    if (!isSupportL1L2Role) {
      setTabIndex(2);
    }
  }, []);
  useEffect(() => {
    dispatch(commonActions.hmplusGlobalReleasesList(analyzerInfo.key));

    if (!receivedState.version || !receivedState.uploadedDate) {
      dispatch(
        hmplusGlobalReleaseActions.getHMPLUSGlobalRelease(analyzerInfo.key)
      ).then(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (version) {
      dispatch(
        fuseAdoptionActions.getFuseAdoptionData({
          version,
          fuseDevice: analyzerInfo.target,
        })
      );
    }
  }, [version]);

  useEffect(() => {
    if (!receivedState.version && hmplusGlobalReleaseData) {
      setVersion(hmplusGlobalReleaseData.version);
      setFromDate(
        clampToTwoMonths(hmplusGlobalReleaseData.releaseDateInMillis)
      );
    }
  }, [hmplusGlobalReleaseData]);

  const handleTabChange = (event, newValue) => {
    if (isSupportL1L2Role) {
      return;
    }
    setTabIndex(newValue);
    if (newValue === 0) {
      history.push(`/dashboard/${analyzerInfo.segment}/releases`);
    } else if (newValue === 1) {
      history.push(`/dashboard/${analyzerInfo.segment}/files`);
    }
  };

  const handleVersionChange = (event) => {
    setVersion(event.target.value);
  };

  const handleCheckboxChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleCsvClick = () => {
    const params = checked
      ? [`isLastRecorded=${checked}`]
      : [
          `startDate=${new Date(fromDate).getTime()}`,
          `endDate=${new Date(toDate).getTime()}`,
        ].join("&");
    fuseAdoptionService
      .getAdoptionCSV(analyzerInfo.target, params)
      .catch((error) => {
        setToastMessage(error.message || t("common:errorBoundary.error"));
        setOpen(true);
      });
  };

  const backClick = () => {
    history.push("/dashboard/analyzer-updates");
  };

  return (
    <>
      <SectionHeader
        title={
          <Typography variant="h5" className={classes.title}>
            {t("analyserUpdates:analyzer.adoption.heading", {
              analyzer: analyzerInfo.text,
            })}
          </Typography>
        }
        backBtn={true}
        backBtnLabel={t("analyserUpdates:analyzer.title")}
        backBtnClbk={backClick}
      />

      <StyledTabs
        value={tabIndex}
        indicatorColor="primary"
        onChange={handleTabChange}
        aria-label="adoption-tabs"
      >
        {!isSupportL1L2Role && [
          <StyledTab key="tab1" label={t("analyserUpdates:hmplus.tab1")} />,
          <StyledTab key="tab2" label={t("analyserUpdates:hmplus.tab2")} />,
        ]}
        <StyledTab label={t("analyserUpdates:analyzer.adoption.tab")} />
      </StyledTabs>

      {!isSupportL1L2Role && [
        <TabPanel key={"panel1"} value={tabIndex} index={0} />,
        <TabPanel key={"panel2"} value={tabIndex} index={1} />,
      ]}
      <TabPanel
        value={tabIndex}
        index={isSupportL1L2Role ? 0 : 2}
        className={classes.tabPanel}
      >
        {loading ? (
          <Loader />
        ) : (
          <>
            <Select
              IconComponent={ExpandMoreIcon}
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                getContentAnchorEl: null,
              }}
              id="version-selector"
              name="version"
              value={version}
              displayEmpty
              inputProps={{ "aria-label": "hmplusVersion-file" }}
              onChange={handleVersionChange}
              className={classes.select}
              classes={{ icon: classes.selectIcon }}
              renderValue={(selected) => (
                <>
                  <span className={classes.versionPrefix}>
                    {t("fuseUpdates:Updates.adoption.versionPrefix")}{" "}
                  </span>
                  <span className={classes.versionNumber}>{selected}</span>
                </>
              )}
            >
              {hmplusGlobalReleasesListData?.map((item, index) => (
                <MenuItem value={item} key={index}>
                  {item}
                </MenuItem>
              ))}
            </Select>

            <DeviceStats {...fuseAdoptionData} analyzer={analyzerInfo.text} />

            {/* Date Pickers */}
            <Box className={classes.dateWrapper}>
              {[
                {
                  label: "from",
                  value: fromDate,
                  setter: setFromDate,
                  // "From" date cannot be more than 2 months before "To" and not after "To"
                  minDate: toDate ? subMonths(toDate, 2) : undefined,
                  maxDate: toDate || undefined,
                },
                {
                  label: "to",
                  value: toDate,
                  setter: setToDate,
                  // "To" date cannot be less than "From" and not more than 2 months after "From"
                  minDate: fromDate || undefined,
                  maxDate: fromDate ? addMonths(fromDate, 2) : undefined,
                },
              ].map(({ label, value, setter, minDate, maxDate }) => (
                <div className={classes.dateField} key={label}>
                  <Typography className={classes.dateLabel}>
                    {t(`fuseUpdates:Updates.adoption.${label}`)}
                  </Typography>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      value={value}
                      disableToolbar
                      variant="inline"
                      format={dateFormatMui}
                      margin="dense"
                      autoOk
                      disabled={checked}
                      KeyboardButtonProps={{ "aria-label": "change date" }}
                      emptyLabel={dateFormatSlashed}
                      onChange={setter}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  </MuiPickersUtilsProvider>
                </div>
              ))}

              <Box
                display="flex"
                alignItems="center"
                className={classes.checkboxWrapper}
              >
                <Checkbox
                  checked={checked}
                  onChange={handleCheckboxChange}
                  className={classes.checkbox}
                  color="primary"
                  name="lastRecord"
                />
                <Typography className={classes.checkboxLabel}>
                  {t("fuseUpdates:Updates.adoption.lastRecorded")}
                </Typography>
              </Box>
            </Box>

            <List className={classes.root}>
              <ListItem className={classes.listItem} divider>
                <Typography className={classes.listItemText}>
                  {t("analyserUpdates:analyzer.adoption.exportData", {
                    analyzer: analyzerInfo.text,
                    version: version,
                  })}
                </Typography>
                <Button
                  color="primary"
                  className={classes.button}
                  onClick={handleCsvClick}
                >
                  <GetAppIcon />
                  {t("analyserUpdates:analyzer.adoption.export")}
                </Button>
              </ListItem>
            </List>
          </>
        )}
      </TabPanel>
      <div className={classes.toastPosition}>
        <Toast
          open={open}
          message={toastMessage}
          severity={"error"}
          handleClose={() => setOpen(false)}
          icon={<Error />}
          duration={1000}
        />
      </div>
    </>
  );
}

export { AnalyzerAdoption };
